import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { ChevronRight, Folder, FolderOpen, Home } from "lucide-react";
import { useI18n } from "@/lib/i18n";

export default function FolderPicker({ accountId, value, onChange }) {
  const { t } = useI18n();
  // value: { id, name } — selected folder
  // breadcrumbs: [{ id, name }]
  const [breadcrumbs, setBreadcrumbs] = useState([{ id: "root", name: t("common.root") }]);
  const [folders, setFolders] = useState(null);
  const [loading, setLoading] = useState(false);

  const currentId = breadcrumbs[breadcrumbs.length - 1].id;

  const load = async (id) => {
    if (!accountId) {
      setFolders([]);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get(`/drive/accounts/${accountId}/folders`, { params: { parent: id } });
      setFolders(data.folders);
    } catch (e) {
      setFolders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!accountId) {
      setFolders([]);
      return;
    }
    setBreadcrumbs([{ id: "root", name: t("common.root") }]);
  }, [accountId, t]);

  useEffect(() => {
    if (accountId) load(currentId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, currentId]);

  const handleEnter = (folder) => {
    setBreadcrumbs([...breadcrumbs, { id: folder.id, name: folder.name }]);
  };

  const handleCrumb = (i) => {
    setBreadcrumbs(breadcrumbs.slice(0, i + 1));
  };

  const handleSelect = () => {
    const last = breadcrumbs[breadcrumbs.length - 1];
    onChange({ id: last.id, name: last.id === "root" ? t("common.root") : last.name });
  };

  return (
    <div className="border border-border bg-card">
      <div className="flex flex-wrap items-center gap-1 border-b border-border px-3 py-2 font-mono text-xs">
        {breadcrumbs.map((b, i) => (
          <React.Fragment key={`${b.id}-${i}`}>
            {i > 0 && <ChevronRight className="h-3 w-3 text-muted-foreground" />}
            <button
              type="button"
              onClick={() => handleCrumb(i)}
              className={`hover:text-foreground transition ${i === breadcrumbs.length - 1 ? "text-foreground" : "text-muted-foreground"}`}
              data-testid={`crumb-${i}`}
            >
              {i === 0 ? <Home className="inline h-3 w-3 mr-1 -mt-0.5" /> : null}
              {b.name}
            </button>
          </React.Fragment>
        ))}
      </div>

      <div className="max-h-72 overflow-y-auto">
        {!accountId && (
          <div className="p-6 text-center text-sm text-muted-foreground">{t("wizard.pickAccount")}</div>
        )}
        {accountId && loading && (
          <div className="p-6 text-center font-mono text-xs text-muted-foreground">{t("common.loading")}</div>
        )}
        {accountId && !loading && folders && folders.length === 0 && (
          <div className="p-6 text-center text-sm text-muted-foreground">— no subfolders —</div>
        )}
        {accountId && folders && folders.length > 0 && (
          <ul>
            {folders.map((f) => (
              <li key={f.id}>
                <button
                  type="button"
                  onClick={() => handleEnter(f)}
                  className="flex w-full items-center justify-between gap-2 border-b border-border px-3 py-2 text-left text-sm hover:bg-accent/40 last:border-0"
                  data-testid={`folder-${f.id}`}
                >
                  <span className="flex items-center gap-2 truncate">
                    <Folder className="h-4 w-4 text-[#002FA7]" />
                    {f.name}
                  </span>
                  <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex items-center justify-between border-t border-border bg-muted/30 px-3 py-2">
        <span className="truncate font-mono text-[11px] text-muted-foreground">
          {t("wizard.selectedFolder")}: <span className="text-foreground">{value?.name || breadcrumbs[breadcrumbs.length - 1].name}</span>
        </span>
        <button
          type="button"
          onClick={handleSelect}
          className="rounded-sm bg-foreground px-3 py-1 font-mono text-[11px] uppercase text-background hover:bg-foreground/90"
          data-testid="select-current-folder"
        >
          <FolderOpen className="mr-1 inline h-3 w-3 -mt-0.5" />
          {t("common.confirm")}
        </button>
      </div>
    </div>
  );
}
